import { useState } from 'react'
import { Link, useForm, usePage } from '@inertiajs/react'
import { Button } from '@/shared/ui/button'
import { Label } from '@/shared/ui/label'
import { SearchableSelect } from '@/shared/ui/searchable-select'
import { Textarea } from '@/shared/ui/textarea'
import { NoAccessNotice } from '@/shared/ui/no-access-notice'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog'
import { useCan } from '@/shared/hooks/useCan'
import { Resources, Actions } from '@/shared/rbac'
import { t } from '@/shared/lib/i18n'
import { instrumentPath, transferInstrumentPath } from '@/shared/lib/routes'
import type { CurrentUserInfo } from '@/shared/types/CurrentUserInfo'
import type { WebInstrumentTransferForm } from '@/shared/types/web'

interface Props extends WebInstrumentTransferForm {
  currentUserInfo: CurrentUserInfo | null
  flash: { notice?: string; alert?: string }
}

type TransferFormPageProps = Props & Record<string, unknown>

const InstrumentTransferForm = () => {
  const {
    id: instrumentId,
    instrumentLabel,
    currentSiteId,
    currentDepartmentId,
    currentUnitId,
    currentSiteName,
    currentDepartmentName,
    currentUnitName,
    siteOptions,
    departmentOptions,
    unitOptions,
    transferBlockedTriggeredTasks,
    transferBlockedOpenNonCompliances,
  } = usePage<TransferFormPageProps>().props

  const canSubmit = useCan(Resources.INSTRUMENT_DEPARTMENT, Actions.UPDATE)

  // The move is blocked up front (triggered tasks or open NCs) — surface the reason modal
  // immediately and keep the form read-only, mirroring the server-side guard (REBUILD-421).
  const blockedReason = transferBlockedTriggeredTasks
    ? 'triggered_tasks'
    : transferBlockedOpenNonCompliances
      ? 'open_non_compliances'
      : null
  const [blockDialogOpen, setBlockDialogOpen] = useState(blockedReason !== null)

  const form = useForm({
    site_id: currentSiteId?.toString() ?? '',
    department_id: currentDepartmentId?.toString() ?? '',
    unit_id: currentUnitId?.toString() ?? '',
    comment: '',
  })

  const filteredDepartmentOptions = departmentOptions.filter(
    (department) => department.siteId === Number(form.data.site_id),
  )
  const filteredUnitOptions = unitOptions.filter(
    (unit) => unit.departmentId === Number(form.data.department_id),
  )

  const handleSiteChange = (value: string) => {
    const firstDepartment = departmentOptions.find((d) => d.siteId === Number(value))
    const firstUnit = firstDepartment
      ? unitOptions.find((u) => u.departmentId === firstDepartment.value)
      : undefined
    form.setData((previous) => ({
      ...previous,
      site_id: value,
      department_id: firstDepartment?.value.toString() ?? '',
      unit_id: firstUnit?.value.toString() ?? '',
    }))
  }

  const handleDepartmentChange = (value: string) => {
    const firstUnit = unitOptions.find((u) => u.departmentId === Number(value))
    form.setData((previous) => ({
      ...previous,
      department_id: value,
      unit_id: firstUnit?.value.toString() ?? '',
    }))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    form.transform((data) => ({ transfer: data }))
    form.patch(transferInstrumentPath(instrumentId))
  }

  const isBlocked = blockedReason !== null

  return (
    <div className="space-y-6">
      <div className="rounded-lg border p-6">
        <h1 className="text-2xl font-bold mb-2">{t('instruments.transfer.pageTitle')}</h1>
        <p className="text-sm text-muted-foreground mb-6">{instrumentLabel}</p>

        <dl className="mb-6 grid grid-cols-1 gap-2 text-sm sm:grid-cols-3">
          <div>
            <dt className="text-muted-foreground">
              {t('instruments.transfer.currentLocationTitle')} ·{' '}
              {t('instruments.transfer.siteField')}
            </dt>
            <dd className="font-medium">{currentSiteName ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">{t('instruments.transfer.departmentField')}</dt>
            <dd className="font-medium">{currentDepartmentName ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">{t('instruments.transfer.unitField')}</dt>
            <dd className="font-medium">{currentUnitName ?? '—'}</dd>
          </div>
        </dl>

        <form onSubmit={handleSubmit} className="space-y-4 max-w-xl">
          <div className="space-y-1.5">
            <Label>{t('instruments.transfer.siteField')} *</Label>
            <SearchableSelect
              className="w-full"
              value={form.data.site_id}
              onValueChange={handleSiteChange}
              placeholder={t('instruments.transfer.siteField')}
              disabled={isBlocked}
              options={siteOptions.map((option) => ({
                value: option.value.toString(),
                label: option.label,
              }))}
            />
          </div>

          <div className="space-y-1.5">
            <Label>{t('instruments.transfer.departmentField')} *</Label>
            <SearchableSelect
              key={`dept-${form.data.site_id}`}
              className="w-full"
              value={form.data.department_id}
              onValueChange={handleDepartmentChange}
              placeholder={t('instruments.transfer.departmentField')}
              disabled={isBlocked}
              options={filteredDepartmentOptions.map((option) => ({
                value: option.value.toString(),
                label: option.label,
              }))}
            />
          </div>

          <div className="space-y-1.5">
            <Label>{t('instruments.transfer.unitField')} *</Label>
            <SearchableSelect
              key={`unit-${form.data.department_id}`}
              className="w-full"
              value={form.data.unit_id}
              onValueChange={(value) => form.setData('unit_id', value)}
              placeholder={t('instruments.transfer.unitField')}
              disabled={isBlocked}
              options={filteredUnitOptions.map((option) => ({
                value: option.value.toString(),
                label: option.label,
              }))}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="comment">{t('instruments.transfer.commentField')}</Label>
            <Textarea
              id="comment"
              value={form.data.comment}
              onChange={(e) => form.setData('comment', e.target.value)}
              maxLength={500}
              rows={3}
              disabled={isBlocked}
            />
          </div>

          {!canSubmit && <NoAccessNotice />}

          <div className="flex gap-3 pt-2">
            {canSubmit && (
              <Button type="submit" disabled={form.processing || isBlocked}>
                {t('instruments.transfer.submit')}
              </Button>
            )}
            <Button asChild type="button" variant="outline">
              <Link href={instrumentPath(instrumentId)}>{t('instruments.transfer.cancel')}</Link>
            </Button>
          </div>
        </form>
      </div>

      <AlertDialog open={blockDialogOpen} onOpenChange={setBlockDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('instruments.transfer.blocked.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {blockedReason === 'triggered_tasks'
                ? t('instruments.transfer.blocked.triggered_tasks')
                : t('instruments.transfer.blocked.open_non_compliances')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setBlockDialogOpen(false)}>
              {t('instruments.transfer.blocked.acknowledge')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

export default InstrumentTransferForm
